import { type DBSchema, openDB } from 'idb';

export type LocalSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface LocalCapture {
  id: string;
  ownerId: string;
  rawText: string;
  capturedAt: string;
  syncStatus: LocalSyncStatus;
  syncError?: string;
  syncedAt?: string;
}

interface InboxDatabase extends DBSchema {
  captures: {
    key: string;
    value: LocalCapture;
    indexes: {
      'by-captured-at': string;
      'by-sync-status': LocalSyncStatus;
    };
  };
  meta: {
    key: string;
    value: { key: string; value: string };
  };
}

const database = openDB<InboxDatabase>('ai-inbox', 1, {
  upgrade(db) {
    const captures = db.createObjectStore('captures', { keyPath: 'id' });
    captures.createIndex('by-captured-at', 'capturedAt');
    captures.createIndex('by-sync-status', 'syncStatus');
    db.createObjectStore('meta', { keyPath: 'key' });
  },
});

export async function saveLocalCapture(rawText: string, ownerId: string) {
  const capture: LocalCapture = {
    id: crypto.randomUUID(),
    ownerId,
    rawText,
    capturedAt: new Date().toISOString(),
    syncStatus: 'pending',
  };
  await (await database).put('captures', capture);
  return capture;
}

export async function updateLocalCapture(
  id: string,
  changes: Partial<Omit<LocalCapture, 'id' | 'rawText' | 'capturedAt'>>,
) {
  const db = await database;
  const current = await db.get('captures', id);
  if (!current) return;
  await db.put('captures', { ...current, ...changes });
}

export async function getCapturesToSync(ownerId: string) {
  const db = await database;
  const [pending, failed, syncing] = await Promise.all([
    db.getAllFromIndex('captures', 'by-sync-status', 'pending'),
    db.getAllFromIndex('captures', 'by-sync-status', 'failed'),
    db.getAllFromIndex('captures', 'by-sync-status', 'syncing'),
  ]);
  return [...pending, ...failed, ...syncing]
    .filter((capture) => capture.ownerId === ownerId)
    .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
}

export async function getPendingCaptureCount(ownerId: string) {
  return (await getCapturesToSync(ownerId)).length;
}

export async function getTodayLocalCaptureCount(ownerId: string) {
  const db = await database;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const range = IDBKeyRange.lowerBound(start);
  const captures = await db.getAllFromIndex('captures', 'by-captured-at', range);
  return captures.filter((capture) => capture.ownerId === ownerId).length;
}
